import React, { useEffect, useState } from 'react';
import theme from '~/theme';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { reduce } from 'lodash';
import GravityFormForm from 'gatsby-plugin-gravity-forms';

import { useGravityFormsContext } from '~/context/GravityFormsContext';
import { useLocation } from '~/context/LocationContext';

import {
    input,
    inputError,
    checkbox,
    textarea,
    radio,
    label,
    selectContainer,
    select,
    fieldWrapper,
    fieldError
} from '~/styles/forms';
import { button } from '~/styles/buttons';
import { h2, label as labelFont } from '~/styles/typography';

const FormWrapper = styled.div`
    width: 100%;

    .gravityform__label {
        ${label};
    }

    .gravityform {
        display: flex;
        flex-direction: column;
    }

    .gravityform__error_inform {
        margin-top: 10px;
        order: 10;
    }

    .gform_title {
        ${h2};
        color: ${theme.colors.text.red};
        margin-bottom: 30px;
    }

    .gform_fields {
        display: grid;
        gap: 28px 0;

        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            grid-template-columns: repeat(12, 1fr);
            grid-column: span 6;
            gap: 35px 28px;
        }
    }

    .gfield_full {
        grid-column: span 12;
    }

    .gfield_half {
        grid-column: span 12;

        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            grid-column: span 6;
        }
    }

    .gfield_third {
        grid-column: span 12;

        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            grid-column: span 4;
        }
    }

    .gfield_quarter {
        grid-column: span 12;

        @media (min-width: ${({ theme }) => theme.breakpoint.tablet}) {
            grid-column: span 3;
        }
    }

    .gfield_required {
        color: ${theme.colors.text.red};
    }

    .gravityform__field {
        ${fieldWrapper};
    }

    .gravityform__field--error {
        .gravityform__field__input,
        .gravityform__field__input__checkbox,
        .gravityform__field__input__radio {
            ${inputError};
        }
    }

    .gravityform__field__input__text,
    .gravityform__field__input__email,
    .gravityform__field__input__phone {
        ${input};
    }

    .gravityform__field__checkbox,
    .gravityform__field__radio {
        li {
            margin-bottom: 15px;

            @media (min-width: ${theme.breakpoint.tablet}) {
                margin-bottom: 20px;
            }
        }
    }

    .gravityform__field__input__checkbox {
        ${checkbox};
    }

    .gravityform__field__input__radio {
        ${radio};
    }

    .gfield_checkbox,
    .gfield_radio {
        li {
            display: flex;
            align-items: center;
        }

        label {
            ${labelFont({ theme })};
            margin-left: 10px;
        }
    }

    .gravityform__field__input__textarea {
        ${textarea};
    }

    .ginput_container_select {
        ${selectContainer};
    }

    .gravityform__field__input__select {
        ${select}
    }

    .gravityform__button {
        ${button};
    }

    .gform_footer {
        margin-top: 35px;
        padding-top: 30px;
    }

    .gravityform__field__hidden {
        visibility: hidden;
    }

    .gravityform__error_message {
        ${fieldError};
    }

    ${({ customCss }) => customCss};
`;

const GravityForm = ({ formId, customCss }) => {
    const { forms } = useGravityFormsContext();
    const { utms } = useLocation();

    const [presetValues, setPresetValues] = useState(null);

    const form = forms.find(
        ({ databaseId }) => databaseId.toString() === formId.toString()
    );

    useEffect(() => {
        if (form && form?.formFields && utms) {
            setPresetValues(
                reduce(
                    form.formFields.nodes,
                    function (result, field) {
                        result[`input_${field.databaseId}`] =
                            field.defaultValue;
                        if (field.canPrepopulate && utms[field.inputName]) {
                            result[`input_${field.databaseId}`] =
                                utms[field.inputName];
                        }
                        return result;
                    },
                    {}
                )
            );
        }
    }, [utms, form]);

    if (!form || !form?.formFields || !presetValues) {
        return null;
    }

    return (
        <FormWrapper {...{ customCss }}>
            <GravityFormForm data={form} {...{ presetValues }} />
        </FormWrapper>
    );
};

GravityForm.propTypes = {
    formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default GravityForm;
